import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppNavigate } from '../hooks/useAppNavigate';
import { useAppSelector, useAppDispatch } from '../redux/hooks';
import { clearCart } from '../redux/slices/cartSlice';
import { FaTrash, FaPlus, FaMinus, FaShoppingBag, FaArrowRight, FaEdit } from 'react-icons/fa';
import { MdShoppingCart, MdAccessTime, MdSecurity } from 'react-icons/md';
import { PageBackButton } from '../components';
import { ItemModifierModal } from '../components/ItemModifierModal';
import { RepeatCustomisationModal, DeleteVariantModal } from '../components/modifiers';
import { useMenuItemActions } from '../hooks/useMenuItemActions';
import {
  getCartItemName,
  getCartItemImage,
  formatModifiersForDisplayWithQuantity,
} from '../utils/cartUtils';
import { formatPrice } from '../utils/priceUtils';

const ViewCartPage: React.FC = () => {
  const appNavigate = useAppNavigate();
  const dispatch = useAppDispatch();
  const { items: cartItems, totalItems, totalAmount } = useAppSelector(state => state.cart);

  // 🛒 Use centralized menu item actions hook
  const {
    isModifierModalOpen,
    isRepeatCustomisationModalOpen,
    isDeleteVariantModalOpen,
    currentMenuItem,
    selectedCartItem,
    editMode,
    deleteVariants,
    handleIncreaseQuantityClick,
    handleDecreaseQuantityClick,
    handleEditCartItem,
    handleRepeatCustomisation,
    handleChooseNewCustomisation,
    handleAddToCartFromModal,
    handleUpdateCartItem,
    handleDeleteVariant,
    closeModifierModal,
    closeRepeatCustomisationModal,
    closeDeleteVariantModal,
  } = useMenuItemActions();

  const handleClearCart = () => {
    dispatch(clearCart());
  };

  const handleContinueShopping = () => {
    appNavigate('/menu');
  };

  const handleProceedToCheckout = () => {
    appNavigate('/checkout');
  };



  // Helper function to check if item has editable properties (modifiers only)
  const hasEditableProperties = (cartItem: any) => {
    const hasModifiers = formatModifiersForDisplayWithQuantity(cartItem.selected.modifiers, cartItem.quantity).length > 0;
    return hasModifiers;
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="container mx-auto px-4 py-8">
          <PageBackButton text="Menu" onClick={() => appNavigate('/menu')} />
          <motion.div
            className="flex flex-col items-center justify-center min-h-[60vh] text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <motion.div
              className="w-32 h-32 bg-gradient-to-br from-red-100 to-orange-100 rounded-full flex items-center justify-center mb-8"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <FaShoppingBag className="w-16 h-16 text-red-400" />
            </motion.div>
            
            <h1 className="text-xl font-bold text-gray-900 mb-4">Your Cart is Empty</h1>
            <p className="text-base text-gray-600 mb-8 max-w-md">
              Looks like you haven't added any items yet. Let's fix that!
            </p>
            
            <motion.button
              onClick={handleContinueShopping}
              className="bg-gradient-to-r from-red-500 to-orange-500 text-white px-6 py-3 rounded-xl font-semibold text-sm shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <MdShoppingCart className="w-6 h-6" />
              Browse Menu
              <FaArrowRight className="w-4 h-4" />
            </motion.button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <PageBackButton text="Menu" onClick={() => appNavigate('/menu')} />
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <MdShoppingCart className="w-6 h-6 text-red-500" />
              Your Cart
            </h1>
            <button
              onClick={handleClearCart}
              className="text-red-500 hover:text-red-700 flex items-center gap-1 text-sm"
            >
              <FaTrash className="w-3 h-3" /> Clear Cart
            </button>
          </div>
          <p className="text-gray-600 mt-2 text-sm">
            {totalItems} {totalItems === 1 ? 'item' : 'items'} • {formatPrice(totalAmount)}
          </p>
        </div>

        {/* Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items - Scrollable Viewport */}
          <div className="lg:col-span-2 max-h-[calc(100vh-250px)] overflow-y-auto pr-2">
            <AnimatePresence>
              {cartItems.map((item, index) => (
                <motion.div
                  key={item.cartItemId}
                  className="bg-white rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 mb-6 overflow-hidden border border-gray-100"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -100, scale: 0.8 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  layout
                >
                  <div className="p-6">
                    <div className="flex gap-6">
                      {/* Item Image */}
                      <motion.div
                        className="flex-shrink-0"
                        whileHover={{ scale: 1.05 }}
                        transition={{ duration: 0.2 }}
                      >
                        {getCartItemImage(item) ? (
                          <img
                            src={getCartItemImage(item)}
                            alt={getCartItemName(item)}
                            className="w-24 h-24 object-cover rounded-xl shadow-sm"
                          />
                        ) : (
                          <div className="w-24 h-24 bg-gradient-to-br from-red-400 to-orange-500 rounded-xl flex items-center justify-center text-white font-bold text-xl">
                            {getCartItemName(item).charAt(0).toUpperCase()}
                          </div>
                        )}
                      </motion.div>

                      {/* Item Details */}
                      <div className="flex-1 min-w-0">
                        {/* Top Row: Product Name and Total Price */}
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-base font-semibold text-gray-900">
                            {getCartItemName(item)}
                          </h3>
                          <span className="text-base font-bold text-gray-900">
                            {formatPrice(item.totalPrice * item.quantity)}
                          </span>
                        </div>

                        {/* Second Row: Modifiers with Edit Icons and Prices */}
                        {formatModifiersForDisplayWithQuantity(item.selected.modifiers, item.quantity).length > 0 && (
                          <div className="mt-2 space-y-1 mb-3">
                            {formatModifiersForDisplayWithQuantity(item.selected.modifiers, item.quantity).map((modifier: any, idx: number) => (
                              <div key={idx} className="flex items-center justify-between text-sm">
                                <div className="flex items-center gap-2">
                                  <span className="text-gray-700 bg-gray-100 px-2 py-1 rounded-md">
                                    {modifier.options}
                                  </span>
                                  {hasEditableProperties(item) && (
                                    <motion.button
                                      onClick={() => handleEditCartItem(item)}
                                      className="p-1 rounded-full transition-colors"
                                      style={{ 
                                        color: '#FA2C37',
                                        backgroundColor: 'transparent'
                                      }}
                                      whileHover={{ 
                                        scale: 1.1,
                                        backgroundColor: 'rgba(250, 44, 55, 0.1)'
                                      }}
                                      whileTap={{ scale: 0.9 }}
                                      title="Edit item"
                                    >
                                      <FaEdit className="w-3 h-3" />
                                    </motion.button>
                                  )}
                                </div>
                                <span className="text-green-600 font-medium">
                                  ({modifier.formattedPrice})
                                </span>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Bottom Row: Quantity Controls and Delete Icon */}
                        <div className="flex items-center justify-between mt-3">
                          <div className="flex items-center bg-gray-100 rounded-xl p-1">
                            <motion.button
                              onClick={() => handleDecreaseQuantityClick(item)}
                              className="w-10 h-10 rounded-lg bg-white hover:bg-gray-50 flex items-center justify-center shadow-sm"
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              <FaMinus className="w-3 h-3 text-gray-600" />
                            </motion.button>
                            
                            <span className="w-12 text-center font-bold text-gray-900">
                              {item.quantity}
                            </span>
                            
                            <motion.button
                              onClick={() => handleIncreaseQuantityClick(item)}
                              className="w-10 h-10 rounded-lg bg-white hover:bg-gray-50 flex items-center justify-center shadow-sm"
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              <FaPlus className="w-3 h-3 text-gray-600" />
                            </motion.button>
                          </div>

                          <motion.button
                            onClick={() => handleDecreaseQuantityClick(item)}
                            className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            title="Remove item"
                          >
                            <FaTrash className="w-4 h-4" />
                          </motion.button>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {/* Right Column: Order Summary - Sticky */}
          <div className="lg:col-span-1">
            {/* Order Summary */}
            <motion.div
              className="bg-white rounded-2xl shadow-lg border border-gray-100 sticky top-24"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <div className="bg-gradient-to-r from-red-500 to-orange-500 p-6 text-white">
                <h2 className="text-base font-bold">Order Summary</h2>
                <p className="text-red-100 text-sm">{totalItems} {totalItems === 1 ? 'item' : 'items'}</p>
              </div>

              <div className="p-6 space-y-4">
                <div className="pt-4">
                  <div className="flex justify-between text-base font-bold">
                    <span>Total</span>
                    <span className="text-red-600">{formatPrice(totalAmount)}</span>
                  </div>
                </div>

                {/* Estimated Time */}
                <div className="bg-gray-50 rounded-xl p-4 flex items-center gap-3">
                  <MdAccessTime className="w-5 h-5 text-gray-600" />
                  <div>
                    <p className="font-medium text-gray-900 text-sm">Estimated Time</p>
                    <p className="text-xs text-gray-600">30-45 minutes</p>
                  </div>
                </div>

                {/* Security Badge */}
                <div className="bg-green-50 rounded-xl p-4 flex items-center gap-3">
                  <MdSecurity className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="font-medium text-green-900 text-sm">Secure Checkout</p>
                    <p className="text-xs text-green-700">Your payment is protected</p>
                  </div>
                </div>
              </div>

              <div className="p-6 pt-0 space-y-3">
                <motion.button
                  onClick={handleProceedToCheckout}
                  className="w-full bg-gradient-to-r from-red-500 to-orange-500 text-white py-3 rounded-xl font-bold text-sm shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Proceed to Checkout
                  <FaArrowRight className="w-5 h-5" />
                </motion.button>

                <motion.button
                  onClick={handleContinueShopping}
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-900 py-2 rounded-xl font-medium text-sm flex items-center justify-center gap-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <MdShoppingCart className="w-5 h-5" />
                  Continue Shopping
                </motion.button>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Global Modals from useMenuItemActions */}
      <ItemModifierModal
        isOpen={isModifierModalOpen}
        onClose={closeModifierModal}
        onAddToCart={editMode ? handleUpdateCartItem : handleAddToCartFromModal}
        initialModifiers={editMode ? selectedCartItem?.selected?.modifiers : undefined}
        initialQuantity={editMode ? selectedCartItem?.quantity : 1}
        editMode={editMode}
      />

      <RepeatCustomisationModal
        isOpen={isRepeatCustomisationModalOpen}
        itemName={currentMenuItem?.name || ''}
        modifiers={selectedCartItem?.selected?.modifiers || []}
        onRepeat={handleRepeatCustomisation}
        onChoose={handleChooseNewCustomisation}
        onClose={closeRepeatCustomisationModal}
      />

      <DeleteVariantModal
        isOpen={isDeleteVariantModalOpen}
        itemName={currentMenuItem?.name || ''}
        variants={deleteVariants}
        onUpdateQuantity={handleDeleteVariant}
        onClose={closeDeleteVariantModal}
      />
    </div>
  );
};

export default ViewCartPage;
